import { px } from '@adsk/alloy-react-helpers';

import {
  AlloyLegacyThemeDesignTokens,
  PanelDesignTokens,
  WeaveTheme,
} from '../types';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

export const getPanelTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): PanelDesignTokens => ({
  container: {
    _: {
      'background-color': theme.tokens.colors.background.primary.value,
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      'flex-shrink': 0,
      'flex-wrap': 'wrap',
      'flex-direction': 'column',
      top: 0,
    },
    open: { true: {}, false: {} },
    floating: {
      true: {
        position: 'absolute',
        'z-index': theme.zIndex.panel,
        right: 0,
      },
      false: {},
    },
    compoundVariants: [
      {
        open: 'true',
        floating: 'true',
        styles: { 'box-shadow': theme.shadows.highElevation },
      },
      {
        open: 'true',
        floating: 'false',
        styles: {
          'box-shadow': `0 0 0 1px ${theme.tokens.colors.border.subtle.value}`,
        },
      },
    ],
  },
  contents: {
    open: {
      true: { 'max-width': '100%' },
      false: {},
    },
  },
  headerContainer: {
    'padding-top': px(theme.spacing.M),
    'padding-right': px(theme.spacing.M),
    'padding-bottom': px(theme.spacing.M),
    'padding-left': px(theme.spacing.M),
  },
  header: {
    title: {
      'font-family': theme.typography.heading2.fontFamily,
      'font-weight': String(theme.typography.heading2.fontWeight),
      'font-size': theme.typography.heading2.fontSize,
      'line-height': theme.typography.heading2.lineHeight,
    },
  },
  child: {
    'padding-top': px(theme.spacing.M),
    'padding-right': px(theme.spacing.M),
    'padding-bottom': px(theme.spacing.M),
    'padding-left': px(theme.spacing.M),
  },
  subHeader: {
    paddingTop: px(theme.spacing.XXS + 2),
    paddingRight: px(theme.spacing.M),
    paddingBottom: px(theme.spacing.XXS + 2),
    paddingLeft: px(theme.spacing.M),
  },
  footer: {
    paddingTop: px(theme.spacing.S),
    paddingRight: px(theme.spacing.M),
    paddingBottom: px(theme.spacing.M),
    paddingLeft: px(theme.spacing.M),
  },
  divider: {
    'margin-left': px(theme.spacing.M),
    width: 'calc(100% - 16px - 16px)',
  },
  resize: {
    'z-index': theme.zIndex.panel,
    'gradient-edge-color': theme.colors.adskBlue100,
    'gradient-center-color': theme.colors.adskBlue500,
  },
  resizePanel: {
    'border-right-color': theme.colors.charcoal100,
    'box-shadow-color': theme.colors.charcoal200,
  },
});

export const getPanelTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): PanelDesignTokens => {
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;
  const popover = tokens?.component?.popover;
  const fontSize = generic?.['font-size'];
  const lineHeight = generic?.['line-height'];

  const allPopoverTokensDefined =
    popover?.['box-shadow-x'] &&
    popover?.['box-shadow-y'] &&
    popover?.['box-shadow-blur'] &&
    popover?.['box-shadow-color'];

  const popoverShadow = allPopoverTokensDefined
    ? `${popover['box-shadow-x']} ${popover['box-shadow-y']} ${popover['box-shadow-blur']} ${popover['box-shadow-color']}`
    : undefined;

  return {
    container: {
      _: {
        'background-color': generic?.color?.white?.[100],
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        'flex-shrink': 0,
        'flex-wrap': 'wrap',
        'flex-direction': 'column',
        top: 0,
      },
      open: { true: {}, false: {} },
      floating: {
        true: {
          position: 'absolute',
          'z-index': 1,
          right: 0,
        },
        false: {},
      },
      compoundVariants: [
        {
          open: 'true',
          floating: 'true',
          styles: popoverShadow ? { 'box-shadow': popoverShadow } : {},
        },
        {
          open: 'true',
          floating: 'false',
          styles: semantic?.['border-color']?.divider?.light
            ? {
                'box-shadow': `0 0 0 1px ${semantic['border-color'].divider.light}`,
              }
            : {},
        },
      ],
    },
    contents: {
      open: {
        true: { 'max-width': '100%' },
        false: {},
      },
    },
    headerContainer: {
      'padding-top': generic?.spacing?.[16],
      'padding-right': generic?.spacing?.[16],
      'padding-bottom': generic?.spacing?.[16],
      'padding-left': generic?.spacing?.[16],
    },
    header: {
      title: {
        'font-family': fontFamily,
        'font-weight': '400',
        'font-size': fontSize?.[20],
        'line-height': lineHeight?.[26],
      },
    },
    child: {
      'padding-top': generic?.spacing?.[16],
      'padding-right': generic?.spacing?.[16],
      'padding-bottom': generic?.spacing?.[16],
      'padding-left': generic?.spacing?.[16],
    },
    subHeader: {
      paddingTop: generic?.spacing?.[8],
      paddingRight: generic?.spacing?.[16],
      paddingBottom: generic?.spacing?.[8],
      paddingLeft: generic?.spacing?.[16],
    },
    footer: {
      paddingTop: generic?.spacing?.[12],
      paddingRight: generic?.spacing?.[16],
      paddingBottom: generic?.spacing?.[16],
      paddingLeft: generic?.spacing?.[16],
    },
    divider: {
      'margin-left': generic?.spacing?.[16],
      width: 'calc(100% - 16px - 16px)',
    },
    resize: {
      'z-index': 1,
      'gradient-edge-color': generic?.color?.blue?.[100]?.[100],
      'gradient-center-color': generic?.color?.blue?.[500]?.[100],
    },
    resizePanel: {
      'border-right-color': semantic?.['border-color']?.divider?.light,
      'box-shadow-color': semantic?.['border-color']?.divider?.light,
    },
  };
};
